import React, { useState, useEffect, useCallback } from 'react';
import { Brain, Trash2, Loader2, Plus, Check, X, Ban } from 'lucide-react';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';

interface MemoriesSectionProps {
  workspace: any;
  workspaceId: string;
  api: any;
}

interface Memory {
  id: string;
  memory_type: 'fact' | 'preference' | 'event' | 'action';
  content: string;
  created_at: string;
  active: boolean;
}

interface RejectedMemory {
  id: string;
  memory_type: 'fact' | 'preference' | 'event' | 'action';
  content: string;
  normalized_content: string;
  reason: string | null;
  created_at: string;
}

type MemoryType = 'fact' | 'preference' | 'event' | 'action';
type TabType = 'active' | 'rejected';

const MEMORY_TYPES: { value: MemoryType; label: string; description: string }[] = [
  { value: 'fact', label: 'Fact', description: 'Objective information' },
  { value: 'preference', label: 'Preference', description: 'User preferences' },
  { value: 'event', label: 'Event', description: 'Historical occurrences' },
  { value: 'action', label: 'Action', description: 'Actions taken' },
];

const MAX_CONTENT_LENGTH = 1000;

const MemoriesSection: React.FC<MemoriesSectionProps> = ({
  workspace,
  workspaceId,
  api,
}) => {
  const { isReadOnly } = useReadOnlyMode();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('active');
  
  // Active memories state
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Rejected memories state
  const [rejectedMemories, setRejectedMemories] = useState<RejectedMemory[]>([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedError, setRejectedError] = useState<string | null>(null);
  const [deletingRejectedId, setDeletingRejectedId] = useState<string | null>(null);
  const [rejectedFetched, setRejectedFetched] = useState(false);

  // Add memory form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemoryType, setNewMemoryType] = useState<MemoryType>('fact');
  const [newMemoryContent, setNewMemoryContent] = useState('');
  const [addingMemory, setAddingMemory] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const fetchMemories = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/workspaces/${workspaceId}/memories/`);
      const data = response.data || response;
      setMemories(data.memories || []);
    } catch (err: any) {
      console.error('Error loading memories:', err);
      if (err.response?.status === 503) {
        setError('Memory service is currently unavailable');
      } else {
        setError('Failed to load memories');
      }
      setMemories([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, api]);

  const fetchRejectedMemories = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setRejectedLoading(true);
      setRejectedError(null);
      const response = await api.get(`/workspaces/${workspaceId}/memories/rejections`);
      const data = response.data || response;
      setRejectedMemories(data.rejected_memories || []);
    } catch (err: any) {
      console.error('Error loading rejected memories:', err);
      if (err.response?.status === 503) {
        setRejectedError('Memory service is currently unavailable');
      } else {
        setRejectedError('Failed to load rejected memories');
      }
      setRejectedMemories([]);
    } finally {
      setRejectedLoading(false);
      setRejectedFetched(true);
    }
  }, [workspaceId, api]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  // Fetch rejected memories when switching to that tab
  useEffect(() => {
    if (activeTab === 'rejected' && !rejectedFetched && !rejectedLoading) {
      fetchRejectedMemories();
    }
  }, [activeTab, rejectedFetched, rejectedLoading, fetchRejectedMemories]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleAddMemory = async () => {
    const trimmedContent = newMemoryContent.trim();
    if (!trimmedContent) {
      setToast({ message: 'Memory content is required', type: 'error' });
      return;
    }

    if (trimmedContent.length > MAX_CONTENT_LENGTH) {
      setToast({ message: `Content exceeds ${MAX_CONTENT_LENGTH} characters`, type: 'error' });
      return;
    }

    try {
      setAddingMemory(true);
      await api.post(`/workspaces/${workspaceId}/memories/`, {
        memory_type: newMemoryType,
        content: trimmedContent,
      });

      // Reset form
      setNewMemoryContent('');
      setNewMemoryType('fact');
      setShowAddForm(false);

      setToast({ message: 'Memory creation started', type: 'success' });

      // Refresh list after a short delay to allow async processing
      setTimeout(() => fetchMemories(), 1000);
    } catch (err: any) {
      console.error('Error creating memory:', err);
      setToast({
        message: err.detail || err.message || 'Failed to create memory',
        type: 'error',
      });
    } finally {
      setAddingMemory(false);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (isReadOnly) return;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this memory?')) {
      return;
    }

    try {
      setDeletingId(memoryId);
      await api.delete(`/workspaces/${workspaceId}/memories/${memoryId}`);
      
      // Optimistic UI update
      setMemories((prev) => prev.filter((m) => m.id !== memoryId));
    } catch (err: any) {
      console.error('Error deleting memory:', err);
      alert('Failed to delete memory. Please try again.');
      // Refetch to ensure UI is in sync
      fetchMemories();
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteRejectedMemory = async (rejectionId: string) => {
    if (isReadOnly) return;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this rejected memory? The agent may suggest this memory again in the future.')) {
      return;
    }

    try {
      setDeletingRejectedId(rejectionId);
      await api.delete(`/workspaces/${workspaceId}/memories/rejections/${rejectionId}`);
      
      // Optimistic UI update
      setRejectedMemories((prev) => prev.filter((m) => m.id !== rejectionId));
      setToast({ message: 'Rejected memory deleted', type: 'success' });
    } catch (err: any) {
      console.error('Error deleting rejected memory:', err);
      alert('Failed to delete rejected memory. Please try again.');
      // Refetch to ensure UI is in sync
      fetchRejectedMemories();
    } finally {
      setDeletingRejectedId(null);
    }
  };

  const getMemoryTypeColor = (type: string) => {
    switch (type) {
      case 'fact':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'preference':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'event':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'action':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Group memories by type
  const groupedMemories = memories.reduce((acc, memory) => {
    if (!acc[memory.memory_type]) {
      acc[memory.memory_type] = [];
    }
    acc[memory.memory_type].push(memory);
    return acc;
  }, {} as Record<string, Memory[]>);

  // Group rejected memories by type
  const groupedRejectedMemories = rejectedMemories.reduce((acc, memory) => {
    if (!acc[memory.memory_type]) {
      acc[memory.memory_type] = [];
    }
    acc[memory.memory_type].push(memory);
    return acc;
  }, {} as Record<string, RejectedMemory[]>);

  const typeOrder: Array<'fact' | 'preference' | 'event' | 'action'> = [
    'fact',
    'preference',
    'event',
    'action',
  ];

  const renderActiveMemories = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      );
    }

    if (memories.length === 0) {
      return (
        <div className="text-center py-12 bg-neutral-50 dark:bg-neutral-800/30 rounded-xl border border-neutral-200 dark:border-neutral-700">
          <Brain className="w-12 h-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
            No memories yet
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            As you interact with the agent, important information will be remembered here
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {typeOrder.map((type) => {
          const typeMemories = groupedMemories[type];
          if (!typeMemories || typeMemories.length === 0) return null;

          return (
            <div key={type}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3 uppercase tracking-wider">
                {type}s ({typeMemories.length})
              </h3>
              <div className="space-y-3">
                {typeMemories.map((memory) => (
                  <div
                    key={memory.id}
                    className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded ${getMemoryTypeColor(
                              memory.memory_type
                            )}`}
                          >
                            {memory.memory_type}
                          </span>
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            {formatDate(memory.created_at)}
                          </span>
                        </div>
                        <p className="text-neutral-900 dark:text-white break-words">
                          {memory.content}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteMemory(memory.id)}
                        disabled={deletingId === memory.id || isReadOnly}
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isReadOnly ? 'Upgrade to delete memories' : 'Delete memory'}
                      >
                        {deletingId === memory.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRejectedMemories = () => {
    if (rejectedLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      );
    }

    if (rejectedError) {
      return (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl">
          <p className="text-red-700 dark:text-red-400">{rejectedError}</p>
        </div>
      );
    }

    if (rejectedMemories.length === 0) {
      return (
        <div className="text-center py-12 bg-neutral-50 dark:bg-neutral-800/30 rounded-xl border border-neutral-200 dark:border-neutral-700">
          <Ban className="w-12 h-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
            No rejected memories
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            When you reject a memory suggestion, it will appear here to prevent future suggestions
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {typeOrder.map((type) => {
          const typeMemories = groupedRejectedMemories[type];
          if (!typeMemories || typeMemories.length === 0) return null;

          return (
            <div key={type}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3 uppercase tracking-wider">
                {type}s ({typeMemories.length})
              </h3>
              <div className="space-y-3">
                {typeMemories.map((memory) => (
                  <div
                    key={memory.id}
                    className="bg-white dark:bg-neutral-800 border border-red-200 dark:border-red-900/50 rounded-xl p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded ${getMemoryTypeColor(
                              memory.memory_type
                            )}`}
                          >
                            {memory.memory_type}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                            rejected
                          </span>
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            {formatDate(memory.created_at)}
                          </span>
                        </div>
                        <p className="text-neutral-900 dark:text-white break-words">
                          {memory.content}
                        </p>
                        {memory.reason && (
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 italic">
                            Reason: {memory.reason}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteRejectedMemory(memory.id)}
                        disabled={deletingRejectedId === memory.id || isReadOnly}
                        className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isReadOnly ? 'Upgrade to delete rejected memories' : 'Delete rejected memory (allow future suggestions)'}
                      >
                        {deletingRejectedId === memory.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${
            toast.type === 'success'
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <Brain className="w-6 h-6 text-brand-primary" />
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
            Workspace Memories
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Information remembered about this workspace from previous conversations
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-neutral-200 dark:border-neutral-700">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active'
              ? 'border-brand-primary text-brand-primary'
              : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
          }`}
        >
          Active Memories
          {memories.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-neutral-200 dark:bg-neutral-700">
              {memories.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('rejected')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'rejected'
              ? 'border-brand-primary text-brand-primary'
              : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white'
          }`}
        >
          Rejected Memories
          {rejectedMemories.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
              {rejectedMemories.length}
            </span>
          )}
        </button>
      </div>

      {error && activeTab === 'active' && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Add Memory Button - Only shown on active tab */}
      {activeTab === 'active' && !showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          disabled={isReadOnly}
          className={`mb-6 px-4 py-2 rounded-xl flex items-center gap-2 transition-colors ${
            isReadOnly
              ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
              : 'bg-brand-action hover:bg-brand-action-hover text-white dark:text-black'
          }`}
          title={isReadOnly ? 'Upgrade to add memories' : 'Add a new memory'}
        >
          <Plus size={16} />
          Add Memory
        </button>
      )}

      {/* Add Memory Form - Only shown on active tab */}
      {activeTab === 'active' && showAddForm && (
        <div className="mb-6 p-4 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
            Add New Memory
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Memory Type <span className="text-red-500">*</span>
              </label>
              <select
                value={newMemoryType}
                onChange={(e) => setNewMemoryType(e.target.value as MemoryType)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action"
              >
                {MEMORY_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={newMemoryContent}
                onChange={(e) => setNewMemoryContent(e.target.value)}
                placeholder="Enter memory content..."
                rows={4}
                maxLength={MAX_CONTENT_LENGTH}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-action resize-none"
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 text-right">
                {newMemoryContent.length}/{MAX_CONTENT_LENGTH} characters
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleAddMemory}
                disabled={addingMemory || !newMemoryContent.trim()}
                className="px-4 py-2 bg-brand-action hover:bg-brand-action-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                {addingMemory ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Add Memory
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewMemoryContent('');
                  setNewMemoryType('fact');
                }}
                className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg flex items-center gap-2 transition-colors text-neutral-700 dark:text-neutral-300"
              >
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'active' ? renderActiveMemories() : renderRejectedMemories()}
    </div>
  );
};

export default MemoriesSection;
